import React, {FC, useEffect, useMemo, useRef, useState} from 'react';
import styled from 'styled-components';

import {computeMultiSelectCheckboxState} from '../../../../../../../../src/components/common/table/helpers/tableHelpers';
import {AsyncTableSource} from '../../../../../../../../src/components/common/table/source/asyncTableSource';
import {TableMultiSelectHeaderRow} from '../../../../../../../../src/components/common/table/tableMultiSelectHeaderRow';
import {TableRowRenderOptions} from '../../../../../../../../src/components/common/table/tableRow';
import {
  VirtualTable,
  VirtualTableHandle,
} from '../../../../../../../../src/components/common/table/virtualTable';
import {
  VirtualTableRowRenderer,
  VirtualTableRowSiblingData,
} from '../../../../../../../../src/components/common/table/virtualTableBase';
import {buildContactsManagerCardGroupContactsUrl} from '../../../../../../../../src/helpers/contactsManagerHelpers';
import {TableTokenPaginationDataModel} from '../../../../../../../../src/models/table/tablePaginationDataModel';
import {ContactsManagerTableStatusesEnum} from '../../../../../../../../src/store/panels/contacts/contactsManager/table/contactsManagerTableState';
import {Plural} from '../../../../../../../shared/helpers/src/internationalization/react';
import {
  AsyncHandlerOf,
  Handler,
  HandlerOf,
  Renderer,
} from '../../../../../../../shared/helpers/src/react/reactHelpers';
import {CancelToken} from '../../../../../../../shared/helpers/src/utilities/asyncCancelHelpers';
import {VisualSizesEnum} from '../../../../../../../shared/ui/src/styles/commonStyles';
import {useContactsManagerContext} from '../../contactsManagerContext';
import {cardGroupsTableColumns} from '../common/cardGroups/cardGroupsTableConfig';
import {CardGroupsTableRowContainer} from '../common/cardGroups/cardGroupsTableRowContainer';
import {CardGroupsTableMultiDeleteActionContainer} from '../common/cardGroups/multiSelectActions/cardGroupsTableMultiDeleteActionContainer';
import {computeBulkSelectedValues} from '../common/tableHelpers';

/*
 * Props.
 */

interface ListsTableProps {
  cardGroupIds: ReadonlyArray<number>;
  tableStatus?: ContactsManagerTableStatusesEnum;
  paginationData?: TableTokenPaginationDataModel;
  isInMultiSelectMode: boolean;
  selectedCardGroupIds: ReadonlyArray<number>;
  onAddSelectedCardGroupIds: (cardGroupIds: ReadonlyArray<number>, isInMultiSelectMode?: boolean) => void;
  onRemoveSelectedCardGroupIds: HandlerOf<ReadonlyArray<number>>;
  onFetchTableDataAsync: AsyncHandlerOf<CancelToken>;
  renderEmpty: Renderer;
  onReset: Handler;
  navigateToLink: HandlerOf<string>;
}

/*
 * Styles.
 */

const StyledTableContainerDiv = styled.div`
  flex: 1;
  overflow: hidden;
  padding: 0 48px 0 0;
  position: relative;
`;

/*
 * Component.
 */

export const ListsTable: FC<ListsTableProps> = ({
  cardGroupIds,
  tableStatus,
  paginationData,
  isInMultiSelectMode,
  selectedCardGroupIds,
  onAddSelectedCardGroupIds,
  onRemoveSelectedCardGroupIds,
  onFetchTableDataAsync,
  renderEmpty,
  onReset,
  navigateToLink,
}) => {
  const tableRef = useRef<VirtualTableHandle>();
  const [lastSelectedCardGroupId, setLastSelectedCardGroupId] = useState<number>();
  const {panelId} = useContactsManagerContext();

  // Listen for the FETCH_REQUIRED status set.
  useEffect(() => {
    if (tableStatus === ContactsManagerTableStatusesEnum.FETCH_REQUIRED && tableRef.current) {
      tableRef.current.forceReload();
    }
  }, [tableStatus]);

  const source = useMemo(
    () =>
      new AsyncTableSource({
        onReset: async () => onReset(),
        fetcher: async ({cancelToken}) => onFetchTableDataAsync(cancelToken),
      }),
    [onFetchTableDataAsync, onReset],
  );

  const renderRow: VirtualTableRowRenderer<number> = (
    cardGroupId: number,
    options: TableRowRenderOptions,
    siblingData?: VirtualTableRowSiblingData<number>,
  ) => (
    <CardGroupsTableRowContainer
      isSelected={selectedCardGroupIds.includes(cardGroupId)}
      isPreviousSiblingSelected={selectedCardGroupIds.includes(siblingData?.previous ?? -1)}
      isNextSiblingSelected={selectedCardGroupIds.includes(siblingData?.next ?? -1)}
      cardGroupId={cardGroupId}
      options={options}
      tableColumns={cardGroupsTableColumns}
      onSelect={() => {
        onAddSelectedCardGroupIds([cardGroupId], true);
        setLastSelectedCardGroupId(cardGroupId);
        if (!isInMultiSelectMode) {
          navigateToLink(buildContactsManagerCardGroupContactsUrl(cardGroupId));
        }
      }}
      isInMultiSelectMode={isInMultiSelectMode}
      onMultiSelect={() => {
        onAddSelectedCardGroupIds(
          computeBulkSelectedValues(cardGroupId, lastSelectedCardGroupId, cardGroupIds),
          true,
        );
      }}
      onCheckBoxToggle={(shouldSelect) => {
        if (shouldSelect) {
          onAddSelectedCardGroupIds([cardGroupId], true);
          setLastSelectedCardGroupId(cardGroupId);
        } else {
          onRemoveSelectedCardGroupIds([cardGroupId]);
        }
      }}
    />
  );

  const renderMultiSelectHeader = (onMultiSelectHeaderChange: HandlerOf<boolean>) => (
    <TableMultiSelectHeaderRow
      onMultiSelectChange={onMultiSelectHeaderChange}
      multiSelectStatus={computeMultiSelectCheckboxState(cardGroupIds.length, selectedCardGroupIds.length)}
      renderTotalItemsLabel={() => (
        <Plural
          id="contact-manager-table-total-lists-selected"
          value={selectedCardGroupIds.length}
          one="1 list selected"
          other="# lists selected"
        />
      )}
    >
      {<CardGroupsTableMultiDeleteActionContainer panelId={panelId} />}
    </TableMultiSelectHeaderRow>
  );

  return (
    <StyledTableContainerDiv>
      <VirtualTable<number>
        ref={tableRef}
        columns={cardGroupsTableColumns}
        source={source}
        paginationData={paginationData}
        items={cardGroupIds}
        size={VisualSizesEnum.LARGE}
        renderRow={renderRow}
        renderEmpty={renderEmpty}
        shouldAllowMultiSelect
        isInMultiSelectMode={isInMultiSelectMode}
        renderMultiSelectHeader={renderMultiSelectHeader}
        isSourceUnstable
        onMultiSelectHeaderChange={(checked) => {
          if (checked) {
            onAddSelectedCardGroupIds(cardGroupIds, true);
          } else {
            onRemoveSelectedCardGroupIds(cardGroupIds);
          }
        }}
      />
    </StyledTableContainerDiv>
  );
};
